/**
 * Pluggable error reporter.
 *
 * `packages/common` is consumed by web, browser-extension, admin, hubspot and
 * mobile, so it must not import `logfire` — that would drag an OTel SDK into
 * every one of them. Instead it calls `reportError`, and each app installs a
 * concrete implementation at init.
 *
 * The default is exactly today's behaviour, so apps that have not opted in yet
 * (admin, hubspot, mobile) keep working untouched. Adding one later is a single
 * `setErrorReporter(...)` call.
 */

export type ErrorReporter = (message: string, error: unknown, attributes?: Record<string, unknown>) => void;

const defaultReporter: ErrorReporter = (message, error, attributes) => {
  // eslint-disable-next-line no-console
  console.error(message, error, attributes);
};

/**
 * The installed reporter lives on `globalThis`, NOT in a module variable.
 *
 * `@firmable/telemetry` is in `transpilePackages` (see `apps/api/next.config.js`),
 * so Next compiles a private copy of this module into every server entry. A
 * module-scope `let reporter` is therefore per-bundle: `instrumentation.js` calls
 * `setErrorReporter` on ITS copy, while each route bundle keeps a copy still
 * pointing at `defaultReporter`. Every `reportError` from a route then went to
 * `console.error` and never reached Logfire — silently, because falling back to
 * console is indistinguishable from working.
 *
 * That was not hypothetical: across three days of preview traffic, not one record
 * existed from any of the 32 `reportError` call sites in route and utility code.
 * The only server-side error records came from `instrumentation.ts` itself, which
 * is the one bundle where the setter and the caller share a copy.
 *
 * A well-known symbol on `globalThis` is the same escape hatch `@opentelemetry/api`
 * uses for its provider registry, and the reason `withSpan.ts` resolves a tracer at
 * call time rather than at module scope. Resolve on every call for the same reason:
 * a copy loaded before `register()` ran must still see the reporter installed later.
 */
const REPORTER_KEY = Symbol.for('firmable.telemetry.errorReporter');

type GlobalWithReporter = typeof globalThis & { [REPORTER_KEY]?: ErrorReporter };

export function setErrorReporter(fn: ErrorReporter): void {
  (globalThis as GlobalWithReporter)[REPORTER_KEY] = fn;
}

export function resetErrorReporter(): void {
  delete (globalThis as GlobalWithReporter)[REPORTER_KEY];
}

export const reportError: ErrorReporter = (message, error, attributes) => {
  try {
    const reporter = (globalThis as GlobalWithReporter)[REPORTER_KEY] ?? defaultReporter;
    reporter(message, error, attributes);
  } catch {
    // Telemetry must never turn a handled error into an unhandled one.
  }
};
